/**
 * client/src/lib/session.tsx — who is asking, and the four facts the client must not read from the
 * environment.
 *
 * One `GET /api/v1/session` at mount. **VOX-LAW 20: it is the only route in the product that sets a
 * cookie**, so it is also the only route that can be first — every other call in `lib/api.ts`
 * assumes an actor exists, and an anonymous visitor pressing "Make a voice" is the *primary* flow
 * here, not a degraded one (§2.3: "the anon path is how a grandchild records a grandparent on a
 * laptop in a house that has never seen this site").
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `loading` IS A THIRD STATE AND IT RENDERS NEITHER FACE
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * This is a books.karti.ai scar, quoted in audiobook's `lib/auth.tsx` and repeated here because it
 * is about to be repeated in the same place: rendering the door while the session request is in
 * flight flashes the wrong page at somebody who already has a key, on *every single load*, and on
 * `/` that is the first paint of the site.
 *
 * It costs more here than there. audiobook's wrong first paint is a shelf somebody scrolls past.
 * Ours is a 68-year-old watching a page change its mind about what it is, in the first two seconds
 * of the only chance this product gets.
 *
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `app` — the three facts, and why they arrive over the wire
 * ════════════════════════════════════════════════════════════════════════════════════════════════
 * `SessionEnvelope.app` carries `publicOrigin`, `ttsConfigured` and `helperName`. None of them is
 * `import.meta.env`, and that is deliberate:
 *
 *   · **`helperName`** is the copy's `{helper}` — the word "Karti" appears in eleven family-facing
 *     sentences and in none of them is it a literal in this repo. It is `VOX_HELPER_NAME` on the
 *     deployment, because the second family to use this will not be his.
 *   · **`ttsConfigured`** is `false` while `VOX_TTS_MODE=none`, which is the correct setting today
 *     (spark-1 is down by design). §3.14: "Discovering this at the end is the failure; choosing it
 *     at the start is not." A build-time flag would be baked into a bundle that outlives the outage
 *     by weeks, so the studio would offer the deferred choice on a day the GPU was up.
 *   · **`publicOrigin`** is what the claim link is printed against (§2.4, in large type, on a screen
 *     somebody photographs). `window.location.origin` is the wrong source: through the tailnet the
 *     app answers on `100.92.185.76:8912`, and a link printed with that in it is a link that works
 *     from exactly one house.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import type { Actor } from "../../../shared/actor.ts";
import { isUser } from "../../../shared/actor.ts";
import type { SessionEnvelope } from "../../../shared/protocol.ts";
import { ApiError, api } from "./api.ts";

interface SessionValue {
  /** A signed-in `usr_`, a server-minted guest `gst_`, or null when the request has not answered. */
  actor: Actor | null;
  /** Null only while `loading` or `unreachable`. Never partially populated. */
  app: SessionEnvelope["app"] | null;
  /** True until `/session` has answered. Renders neither face — see the header. */
  loading: boolean;
  /** The request itself failed twice. The UI offers "try again" rather than a door. */
  unreachable: boolean;
  signedIn: boolean;
  isGod: boolean;
  /** After a sign-in or sign-up: take the new envelope without a second round trip. */
  adopt: (env: SessionEnvelope) => void;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<SessionValue | null>(null);

/**
 * How long to wait before the one retry. 700 ms, lifted from audiobook, and the derivation is the
 * same: it is long enough that a TCP handshake lost to a wifi roam has completed by the time we ask
 * again, and short enough that it is inside the window a person will sit still for on a first load.
 */
const RETRY_MS = 700;

export function SessionProvider({ children }: { children: ReactNode }) {
  const [env, setEnv] = useState<SessionEnvelope | null>(null);
  const [loading, setLoading] = useState(true);
  const [unreachable, setUnreachable] = useState(false);

  /**
   * StrictMode double-invokes effects in development. Without this the boot request is issued
   * twice, and because `GET /api/v1/session` is the one route that MINTS A GUEST IDENTITY, two
   * requests from a browser with no cookie yet is two `gst_` rows and a quota subject that changed
   * between the first render and the second (§11.3 meters everything per `actorKey`).
   */
  const booted = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    try {
      setEnv(await api.session());
      setUnreachable(false);
    } catch (err) {
      // A transport failure is not an answer. Retry once, then settle — concluding "signed out" from
      // a blip signs somebody out of a year-long session, and here it also throws away the guest
      // identity that owns the recording they are halfway through making.
      if (err instanceof ApiError && err.offline) {
        await new Promise((r) => setTimeout(r, RETRY_MS));
        try {
          setEnv(await api.session());
          setUnreachable(false);
          return;
        } catch {
          setEnv(null);
          setUnreachable(true);
          return;
        }
      }
      // An actual 4xx/5xx is an answer. It is not "offline", and it is not a signed-in session
      // either — the door is the honest thing to render.
      setEnv(null);
      setUnreachable(false);
    }
  }, []);

  useEffect(() => {
    if (booted.current) return;
    booted.current = true;
    void load().finally(() => setLoading(false));
  }, [load]);

  const adopt = useCallback((next: SessionEnvelope) => {
    setEnv(next);
    setUnreachable(false);
  }, []);

  const signOut = useCallback(async () => {
    // Clear locally even if the request fails: the cookie may already be gone, and leaving somebody
    // "signed in" against a server that disagrees is worse than a stale row. Then re-boot, because
    // signing out does not make you nobody — it makes you a guest, and a guest is a real identity
    // here that a claim link can still be resolved against.
    await api.signOut().catch(() => {});
    await load();
  }, [load]);

  const value = useMemo<SessionValue>(
    () => ({
      actor: env?.actor ?? null,
      app: env?.app ?? null,
      loading,
      unreachable,
      signedIn: isUser(env?.actor ?? null),
      // Derived from the actor the server sent, never from an email compared in the browser. The
      // server computes it live from `users.email ∈ VOX_ADMIN_EMAILS` per request (§2.3) precisely so
      // that removing an address from the environment does not leave a year-long session holding
      // godmode.
      isGod: !!env?.actor && env.actor.kind === "user" && env.actor.isAdmin,
      adopt,
      signOut,
      refresh: load,
    }),
    [env, loading, unreachable, adopt, signOut, load],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionValue {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useSession() outside <SessionProvider>");
  return ctx;
}

/**
 * The copy's `{helper}`.
 *
 * Returns `null` rather than a guess while the envelope is loading, and every family page treats
 * null as "not ready to render yet". A default of `"Karti"` here would be a literal in the one repo
 * whose whole copy layer exists so that the words live in exactly one file — and it would render the
 * wrong person's name on the second family's screen without anything failing.
 */
export function useHelper(): string | null {
  return useSession().app?.helperName ?? null;
}
